import { CheckoutParams } from "common/types";
import { toNumber } from "common/validation/utils";
import { InstallmentPlan } from "univapay-node/dist/cjs";

// Query params that should be dropped when using the link form
export const QUERY_PARAMS_TO_REMOVE = [
    "widgetSize",

    // callbacks
    "autoSubmit",
    "autoSubmitOnError",
    "onSuccess",
    "onPending",
    "callback",
    "onError",
    "beforeClosing",
    "onTokenCreated",
    "onChargeCreated",
    "onSubscriptionCreated",
    "onThreeDsAuthorization",
    "onThreeDsAuthorizationSuccess",
    "onThreeDsAuthorizationFailure",
    "onThreeDsAuthorizationModalOpened",
    "onValidationError",
    "opened",
    "closed",
    "responsive",

    "qrColor",
    "qrLogoType",
];

export const PARAMETER_KEY_MAPPING = {
    paymentTypes: "paymentMethods",
    tokenType: "type",
    capture: "auth",
    subscriptionInitialAmount: "initialAmount",
    subscriptionStart: "startOn",
    subscriptionStartIn: "startIn",
    installmentPlan: "installmentPlanType",
    subscriptionPeriod: "period",
    subscriptionPlan: "subscriptionPlanType",
    subscriptionStartDayOfMonth: "startDayOfMonth",
    email: "emailAddress",
    exp: "cardExpiry",
    installmentQty: "installmentCycles",
    subscriptionQty: (params: Partial<CheckoutParams>) =>
        params.subscriptionPlan === InstallmentPlan.FIXED_CYCLE_AMOUNT
            ? "subscriptionCycleAmount"
            : "subscriptionCycles",

    inline: "inline",

    // address
    shippingAddressLine1: "addressHouse",
    shippingAddressLine2: "addressHouseComplements",
    shippingAddressCity: "addressCity",
    shippingAddressState: "addressState",
    shippingAddressZip: "addressZipCode",
    shippingAddressCountryCode: "addressCountryCode",
    customerName: "name",
    customerNameKana: "nameKana",
    buyerName: "name",
    buyerNameKana: "nameKana",
    buyerDateOfBirth: "birthday",

    // product quantities
    productCodeQuantities: "productQuantities",

    // Metadata
    univapayReferenceId: "univapay-reference-id",
};

export const EXCLUDED_PARAMETER_KEYS = [];

export const PARAMETER_VALUE_MAPPING = {
    capture: (params: Partial<CheckoutParams>, capture: string) =>
        capture ? (capture === "true" ? "false" : "true") : undefined,
    amount: (params: Partial<CheckoutParams>, amount: string | number) => {
        const formattedAmount = toNumber(amount);
        const initialAmount = toNumber(params.subscriptionInitialAmount);

        if (params.subscriptionPlan === InstallmentPlan.FIXED_CYCLES && params.subscriptionQty) {
            const offsetCycles = !!initialAmount || initialAmount === 0;

            return Math.floor(
                (formattedAmount - (initialAmount || 0)) /
                    (offsetCycles ? toNumber(params.subscriptionQty - 1) : toNumber(params.subscriptionQty))
            );
        }

        if (params.subscriptionPlan === InstallmentPlan.FIXED_CYCLE_AMOUNT && params.subscriptionQty) {
            return formattedAmount - (initialAmount ?? toNumber(params.subscriptionQty));
        }

        return amount;
    },

    subscriptionInitialAmount: (params: Partial<CheckoutParams>, initialAmount: string | number) => {
        const formattedInitialAmount = toNumber(initialAmount);

        if (params.subscriptionQty) {
            if (params.subscriptionPlan === InstallmentPlan.FIXED_CYCLES) {
                return formattedInitialAmount ?? Math.ceil(toNumber(params.amount) / toNumber(params.subscriptionQty));
            }

            if (params.subscriptionPlan === InstallmentPlan.FIXED_CYCLE_AMOUNT) {
                return formattedInitialAmount ?? toNumber(params.subscriptionQty);
            }
        }

        return initialAmount;
    },

    subscriptionQty: (params: Partial<CheckoutParams>, quantity: string | number) => {
        if (params.subscriptionPlan === InstallmentPlan.FIXED_CYCLES && quantity) {
            return toNumber(quantity) - 1;
        }

        return quantity;
    },
};
